/**
 * Trees render pass — instanced low-poly cone+trunk meshes, one per Tree
 * written by gen-trees. Geometry is hardcoded in the vertex shader (72 verts,
 * no index buffer); per-instance attributes come from `gen.trees.instanceBuffer`
 * bound as a vertex buffer with stepMode='instance'.
 *
 * Mirrors render/buildings: empty group(1), camera at group(0), daynight at
 * group(2), shadow at group(3). Instance count copied once at startup from the
 * gen-side atomic counter, drawn each frame via `drawIndirect`.
 */
import { createRenderPipeline } from '../gpu/pipelines.ts';
import { createEmptyBindGroup } from '../gpu/buffers.ts';
import shadowPcfWgsl from './shadow_pcf.wgsl?raw';
import fogWgsl from './fog.wgsl?raw';
import treesWgsl from './trees.wgsl?raw';

const wgsl = treesWgsl + '\n' + shadowPcfWgsl + '\n' + fogWgsl;

export interface TreesPass {
  draw(pass: GPURenderPassEncoder): void;
  destroy(): void;
}

export interface TreesPassOptions {
  presentationFormat: GPUTextureFormat;
  depthFormat: GPUTextureFormat;
  instanceBuffer: GPUBuffer;
  instanceCountBuffer: GPUBuffer;
  maxInstances: number;
  cameraBindGroupLayout: GPUBindGroupLayout;
  dayNightBindGroupLayout: GPUBindGroupLayout;
  shadowBindGroupLayout: GPUBindGroupLayout;
}

const INSTANCE_STRIDE = 16;
const TREE_VERTEX_COUNT = 72; // 48 cone + 24 trunk; matches treeVert() in trees.wgsl
const INDIRECT_DRAW_BYTES = 16;

export function createTreesPass(device: GPUDevice, opts: TreesPassOptions): TreesPass {
  const empty = createEmptyBindGroup(device, 'trees');

  const pipelineLayout = device.createPipelineLayout({
    label: 'trees.pll',
    bindGroupLayouts: [
      opts.cameraBindGroupLayout,
      empty.layout,
      opts.dayNightBindGroupLayout,
      opts.shadowBindGroupLayout,
    ],
  });

  const pipeline = createRenderPipeline(device, wgsl, {
    label: 'trees',
    layout: pipelineLayout,
    buffers: [{
      arrayStride: INSTANCE_STRIDE,
      stepMode: 'instance',
      attributes: [
        { shaderLocation: 0, offset: 0,  format: 'float32x3' }, // pos
        { shaderLocation: 1, offset: 12, format: 'float32' },   // scale
      ],
    }],
    targets: [{ format: opts.presentationFormat }],
    // 'none' to dodge any winding mishaps on the cone base; trees are cheap.
    primitive: { topology: 'triangle-list', cullMode: 'none', frontFace: 'ccw' },
    depthStencil: {
      format: opts.depthFormat,
      depthCompare: 'less',
      depthWriteEnabled: true,
    },
  });

  const indirectBuffer = device.createBuffer({
    label: 'trees.indirect',
    size: INDIRECT_DRAW_BYTES,
    usage: GPUBufferUsage.INDIRECT | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(indirectBuffer, 0, new Uint32Array([TREE_VERTEX_COUNT, 0, 0, 0]));

  const enc = device.createCommandEncoder({ label: 'trees.indirect.populate' });
  enc.copyBufferToBuffer(opts.instanceCountBuffer, 0, indirectBuffer, 4, 4);
  device.queue.submit([enc.finish()]);

  return {
    draw(pass) {
      pass.setPipeline(pipeline);
      pass.setBindGroup(1, empty.bindGroup);
      pass.setVertexBuffer(0, opts.instanceBuffer);
      pass.drawIndirect(indirectBuffer, 0);
    },
    destroy() {
      indirectBuffer.destroy();
    },
  };
}
